import React from 'react';
import classnames from 'classnames';
import { gikClassPrefix } from '@gik/core/constants';
import BackgroundImage from '@gik/ui/BackgroundImage';
import Image from '@gik/ui/Image';
import { Decoder } from '@gik/ui';

const blockName = `${gikClassPrefix}-product-tile`;

export type ProductTileDirection = 'row' | 'column';

export interface IProductTileProps extends React.HTMLAttributes<HTMLDivElement> {
  imageUrl: string;
  toolbar?: React.ReactNode;
  children?: React.ReactNode;
  containImage?: boolean;
  direction?: ProductTileDirection;
  title?: string;
}

function ProductTile({
  className,
  imageUrl,
  toolbar,
  children,
  containImage,
  direction = 'column',
  title,
  ...otherProps
}: IProductTileProps): React.ReactElement {
  const [mouseHovering, setMouseHovering] = React.useState<boolean>(false);
  const blockClasses = classnames([
    blockName,
    { [`${blockName}--row`]: direction === 'row' },
    { [`${blockName}--col`]: direction === 'column' },
    { [`${blockName}--hover`]: mouseHovering },
    className || '',
  ]);

  function onMouseEnter() {
    setMouseHovering(true);
  }

  function onMouseLeave() {
    setMouseHovering(false);
  }

  // do not re-render the contents when mouse enters/leaves
  // TODO: extract to separate component and use React.memo
  const contents = React.useMemo(() => {
    return (
      <>
        {toolbar && <div className={`${blockName}__toolbar`}>{toolbar}</div>}
        {!containImage && <BackgroundImage src={imageUrl} />}
        {containImage && (
          <figure>
            <Image src={imageUrl} />
          </figure>
        )}
        <main>
          {!title && children}
          {title && (
            <div className={`${blockName}__title`}>
              <Decoder text={title} />
            </div>
          )}
        </main>
      </>
    );
  }, [children, containImage, imageUrl, title, toolbar]);

  return (
    <div className={blockClasses} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} {...otherProps}>
      {contents}
    </div>
  );
}

// only re-render when product image or toolbar changes
export default React.memo(ProductTile, (prevProps, nextProps) => {
  return prevProps.imageUrl === nextProps.imageUrl && prevProps.toolbar === nextProps.toolbar;
});
